// src/prototype/roi/useCaseRoi.ts
// ============================================================================
// Per-use-case ROI resolution. The only module in src/prototype/roi/ that knows
// about the opportunity fixture — roiEngine.ts is pure and roiCanon.ts holds
// only stated inputs, so both stay trivially testable.
//
// The featured initiative (opp-1) uses the calibrated clinical driver chain.
// Everything else derives its single value driver from the evaluation model's
// roiEstimate, and takes its investment schedule from INVESTMENT_BY_INITIATIVE
// where a business case has actually been costed.
// ============================================================================

import { getOpportunityById } from '../data/mockOpportunities';
import {
  buildGenericDrivers,
  CARE_COORD_DRIVERS,
  CARE_COORD_ID,
  CARE_COORD_ROI,
  IMPLEMENTATION_COST,
  OPERATING_COST_PER_YEAR,
  ROI_CONFIG,
} from './roiCanon';
import { computeRoiModel, type RoiDriver, type RoiModel } from './roiEngine';

/**
 * Where a business case has been costed, its investment schedule. Sourced from
 * the Execute records' CFO panels — opp-2 carries an additional $250K of
 * clinical model validation, opp-3 is a lighter build.
 *
 * Anything absent falls back to the canon schedule, which is stated rather than
 * guessed: the initiative has not been separately costed yet.
 */
const INVESTMENT_BY_INITIATIVE: Record<
  string,
  { implementationCost: number; operatingCostPerYear: number }
> = {
  'opp-2': { implementationCost: 1_800_000, operatingCostPerYear: OPERATING_COST_PER_YEAR },
  'opp-3': { implementationCost: 900_000, operatingCostPerYear: OPERATING_COST_PER_YEAR },
};

/** Time-to-value strings on the fixture read like '3-6 months' / '6 months'. */
function rampMonthsFromTimeToValue(timeToValue: string | undefined): number {
  if (!timeToValue) return 6;
  const numbers = timeToValue.match(/\d+/g);
  if (!numbers || numbers.length === 0) return 6;
  // A range's upper bound is the honest ramp: '3-6 months' reaches full rate at 6.
  return Number(numbers[numbers.length - 1]);
}

export function getRoiDrivers(useCaseId: string): RoiDriver[] {
  if (useCaseId === CARE_COORD_ID) return CARE_COORD_DRIVERS;

  const opportunity = getOpportunityById(useCaseId) as
    | { roiEstimate?: { low?: number; high?: number }; timeToValue?: string }
    | undefined;

  const investment = INVESTMENT_BY_INITIATIVE[useCaseId];

  return buildGenericDrivers({
    annualLow: opportunity?.roiEstimate?.low ?? 1_200_000,
    annualHigh: opportunity?.roiEstimate?.high ?? 2_800_000,
    implementationCost: investment?.implementationCost ?? IMPLEMENTATION_COST,
    operatingCostPerYear: investment?.operatingCostPerYear ?? OPERATING_COST_PER_YEAR,
    rampMonths: rampMonthsFromTimeToValue(opportunity?.timeToValue),
  });
}

/**
 * The computed model for a use case. Memoised per id: the drivers are canon
 * constants, so recomputing on every render of every surface would be pure waste.
 * The ROI page does NOT use this for its live editing — it holds its own driver
 * state and recomputes as the presenter types.
 */
const cache = new Map<string, RoiModel>();

export function roiForUseCase(useCaseId: string): RoiModel {
  if (useCaseId === CARE_COORD_ID) return CARE_COORD_ROI;

  const cached = cache.get(useCaseId);
  if (cached) return cached;

  const model = computeRoiModel(getRoiDrivers(useCaseId), ROI_CONFIG);
  cache.set(useCaseId, model);
  return model;
}
